import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {NatResidentBicycleParkingUseWithdrawalLocationState} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {
    NatPutBicycleParkingUseWithdrawalApplyRequest,
    NatPutBicycleParkingUseWithdrawalApplyResponse,
} from '../repository/NatPutBicycleParkingUseWithdrawal'
import NatResidentBicycleParkingUseWithdrawalForm from './resident/NatResidentBicycleParkingUseWithdrawalForm'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId?: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentBicycleParkingUseWithdrawalLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    withdrawalArea: Nullable<string>
    withdrawalDate: Nullable<string>
    bicycleCarryingOutDate: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentBicycleParkingUseWithdrawalInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            withdrawalArea: null,
            withdrawalDate: null,
            bicycleCarryingOutDate: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {applyId, condominiumCd, applicationRepo, eventEmitter} = this.props
        const applyInfo = this.props.location.state?.applyInfo

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultWithdrawalArea = applyInfo?.withdrawalArea ?? null
        let defaultWithdrawalDate = applyInfo?.withdrawalDate ?? null
        let defaultBicycleCarryingOutDate = applyInfo?.bicycleCarryingOutDate ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const bicycleParkingUsesWithdrawalDetail = await this.props.asyncCallFunction(
                    applicationRepo.getBicycleParkingUseWithdrawalForResident.bind(
                        applicationRepo, condominiumCd, applyId,
                    ),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (bicycleParkingUsesWithdrawalDetail?.details) {
                    defaultWithdrawalArea = bicycleParkingUsesWithdrawalDetail.details.withdrawalArea
                    defaultWithdrawalDate = bicycleParkingUsesWithdrawalDetail.details.withdrawalDate
                    defaultBicycleCarryingOutDate = bicycleParkingUsesWithdrawalDetail.details.bicycleCarryingOutDate
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            withdrawalArea: defaultWithdrawalArea,
            withdrawalDate: defaultWithdrawalDate,
            bicycleCarryingOutDate: defaultBicycleCarryingOutDate,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutBicycleParkingUseWithdrawalApplyRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutBicycleParkingUseWithdrawalApplyResponse> =
                await this.props.asyncCallFunction(
                    applicationRepo.applyBicycleParkingUseWithdrawal.bind(
                        applicationRepo, condominiumCd, request),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    },
                    {
                        defaultErrorTitle: Messages.EOPCR0014.toString(),
                        defaultErrorTitlePriority: true,
                        replacementDetailErrorMessages: [{
                            targetMessageId: Messages.EOPCA0004,
                            replacementMessageId: Messages.EOPCR0015,
                        }],
                    },
                )

            if (response && response.requestError === null) {
                const locationState: NatResidentBicycleParkingUseWithdrawalLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        withdrawalArea: request.withdrawalArea,
                        withdrawalDate: request.withdrawalDate,
                        bicycleCarryingOutDate: request.bicycleCarryingOutDate,
                    },
                    applyId: this.props.applyId,
                }

                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/bicycleParkingUsesWithdrawal/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/bicycleParkingUsesWithdrawal/${this.props.applyId}`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    自転車置場使用許可解除届
                </PageTitle>
                <Box height={'20px'}/>
                <Box>
                    自転車置場の使用を中止したい場合に提出が必要です。使用規則で定められた期限までに必ず提出してください。
                </Box>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentBicycleParkingUseWithdrawalForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentBicycleParkingUseWithdrawalInputPage)
